import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import "./ActivityNotesTable.css";
import BusinessActivityNotesFilterCard from '../ActivityNotesFilter/BusinessActivityNotesFilterCard';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { setPage, setRowsPerPage, setSort } from '../../../../features/activityNotesSlice';
import { Avatar, AvatarGroup, Chip, Pagination, Stack, TablePagination, TableSortLabel, Typography } from '@mui/material';
import React from 'react';
import { CheckPermissions } from '../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions';
import { selectUserData } from '../../../../features/userSlice';
import { formatDate } from '../../../../utils/helpers';

const ActivityNotesTable = () => {
  const dispatch = useDispatch();
  const userData = useSelector(selectUserData)
  const filteredActivityNotes = useSelector((state: any) => state.activityNotes.filteredActivityNotes);
  const { selectedNotetitle, selectedAssets, selectedStakeholders, selectedNoteDate, selectedLabels, page, rowsPerPage, sort } = useSelector((state: any) => state?.activityNotes);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);

  const handleChangePage = (_: unknown, newPage: number) => {
    dispatch(setPage(newPage));
    // Update the URL param when page changes
    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams);
      updatedParams.set("page", newPage.toString());
      return updatedParams;
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(setRowsPerPage(+event.target.value));
    dispatch(setPage(0));
  };

  const formatDateToDDMMYYYY = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0"); // Get day and pad with leading zero
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Get month (0-11) and pad
    const year = date.getFullYear(); // Get year
    return `${day}/${month}/${year}`; // Return in DD/MM/YYYY format
  };

  const getSortedData = useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction) {
        return arrayToSort;
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;

        if (keytoSort === undefined) {
          return 0;
        }

        let aValue = a[keytoSort];
        let bValue = b[keytoSort];

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (aValue === "") return 1;
        if (bValue === "") return -1;

        // Special case: if sorting by date fields, compare as Date objects
        if (keytoSort === "modified" || keytoSort === "created" || keytoSort === "activity_date") {
          const dateA = new Date(aValue);
          const dateB = new Date(bValue);
          return sort.direction === "asc"
            ? dateA.getTime() - dateB.getTime()
            : dateB.getTime() - dateA.getTime();
        }

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sort]
  );

  const filteredData = useMemo(() => {
    // First apply filters
    const filtered = filteredActivityNotes.filter((notes: any) => {
      const statusAssets = selectedAssets && selectedAssets?.length !== 0 ? selectedAssets.includes(notes?.website_name) : true;
      const statusStakeholder = selectedStakeholders && selectedStakeholders?.length !== 0 ? selectedStakeholders.includes(notes?.note_type) : true;
      const notesName = selectedNotetitle && selectedNotetitle?.length !== 0 ? selectedNotetitle.includes(notes?.note) : true;
      const notesLabels = selectedLabels && selectedLabels?.length !== 0 ? selectedLabels.some((label: any) => notes?.labels?.includes(label)) : true;

      const isDateInRange =
        Array.isArray(selectedNoteDate) &&
          selectedNoteDate.length === 2 &&
          selectedNoteDate[0] &&
          selectedNoteDate[1]
          ? selectedNoteDate[0] === selectedNoteDate[1]
            ? new Date(notes?.created).toDateString() ===
            new Date(selectedNoteDate[0]).toDateString()
            : new Date(notes?.created) >= new Date(selectedNoteDate[0]) &&
            new Date(notes?.created) <=
            new Date(new Date(selectedNoteDate[1]).setHours(23, 59, 59, 999))
          : true;

      return (statusAssets && notesName && statusStakeholder && isDateInRange && notesLabels);
    });

    // Then apply sorting
    return getSortedData(filtered);
  }, [filteredActivityNotes, selectedAssets, selectedStakeholders, selectedNotetitle, selectedLabels, selectedNoteDate, getSortedData]);

  const tableheaders = [
    { id: 0, KEY: "note", label: "Note Title", sortable: true },
    { id: 1, KEY: "website_name", label: "Related Asset", sortable: true },
    { id: 2, KEY: "related_brand", label: "Related Brand", sortable: true },
    { id: 3, KEY: "note_type", label: "Stakeholder", sortable: true },
    { id: 4, KEY: "activity_date", label: "Date", sortable: true },
    { id: 5, KEY: "labels", label: "Labels", sortable: true },
    { id: 6, KEY: "created", label: "Created On", sortable: true },
    { id: 7, KEY: "created_by", label: "Created By", sortable: true },
  ];

  // function to handle click on table headers
  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable
    dispatch(setSort({ keytoSort: header.KEY, direction: header.KEY === sort.keytoSort && sort.direction === "asc" ? "desc" : "asc", })
    );
  };

  useEffect(() => {
    // Get current page from URL searchParams if it exists
    const currentPage = searchParams.get("page");

    // If page exists in URL, use that value (convert to number)
    if (currentPage !== null) {
      dispatch(setPage(Number(currentPage)));
    }
    // Otherwise reset to page 0 (when filters change)
    else {
      dispatch(setPage(0));
      // Also update URL to reflect page 0
      setSearchParams((prevParams) => {
        const updatedParams = new URLSearchParams(prevParams);
        updatedParams.set("page", "0");
        return updatedParams;
      });
    }
    // eslint-disable-next-line
  }, [searchParams, selectedAssets, selectedLabels, selectedNotetitle, selectedStakeholders, selectedNoteDate]);

  useEffect(() => {
    // Only show loading when data is actually being fetched
    // Once filteredActivityNotes is populated (even if empty), we're done loading
    if (filteredActivityNotes !== undefined && filteredActivityNotes !== null) {
      setIsLoading(false);
    }
    // eslint-disable-next-line
  }, [filteredActivityNotes]);

  const search = searchParams.get("search");
  const hasEditActivityNotesPermission = CheckPermissions(
    userData?.data?.products?.assets,
    "overview",
    "edit_activity_notes"
  );

  return (
    <>
      <BusinessActivityNotesFilterCard />
      <TableContainer className="themeWrap" component={Paper} >
        <Table aria-label="collapsible table" size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {tableheaders.map((header) => (
                <TableCell key={header.id}>
                  <TableSortLabel
                    active={sort.keytoSort === header.KEY}
                    direction={
                      sort.keytoSort === header.KEY ? sort.direction : "asc"
                    }
                    onClick={() => handleHeaderClick(header)}
                    disabled={!header.sortable}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body1" color="textSecondary">
                    Loading...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredData?.length > 0 ? (
              <>
                {filteredData?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                  <React.Fragment key={index}>
                    <TableRow
                      key={index}
                      sx={{ "& > *": { borderBottom: "1px solid rgba(224, 224, 224, 1) !important", }, }}>
                      <TableCell align="left">
                        {(() => {
                          const params = new URLSearchParams(searchParams);
                          params.set("noteShareUid", row?.shared_uid || "");
                          if (search) {
                            params.set("search", search);
                          }
                          const queryString = params.toString();
                          return (
                            hasEditActivityNotesPermission === true ? (
                              <RouterLink
                                style={{ textDecoration: "underline", color: "#9C27B0" }}
                                data-tracker="417"
                                to={`/assets/overview/activity_notes/activity_note_details?${queryString}`}
                              >
                                <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                                  {row?.note?.length > 50 ? row.note.slice(0, 50) + "..." : row?.note}
                                </div>
                              </RouterLink>
                            ) : (
                              row?.note?.length > 50 ? row.note.slice(0, 50) + "..." : row?.note
                            )
                          );
                        })()}
                      </TableCell>
                      <TableCell align="left">{row?.website_name}</TableCell>
                      <TableCell align="left">{row?.brand}</TableCell>
                      <TableCell align="left">{row?.note_type}</TableCell>
                      <TableCell align="left">{formatDateToDDMMYYYY(row?.activity_date)}</TableCell>
                      <TableCell align="left">
                        <div className="chip-data">
                          {Array.isArray(row.labels) &&
                            row.labels?.length > 0 ? (
                            <>
                              <Chip
                                label={row.labels[0]}
                                sx={{
                                  backgroundColor: "#7B1FA2",
                                  color: "#ffffff",
                                  height: "26px",
                                }}
                              />
                              <AvatarGroup
                                total={row.labels?.length - 1}
                                variant="circular"
                              ></AvatarGroup>
                            </>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell align="left">
                        {formatDate(row?.created)}
                      </TableCell>
                      <TableCell align="left">
                        <div style={{ display: "flex", alignItems: "center", gap: 5, }}>
                          <Avatar sx={{ width: 24, height: 24 }}>
                            {row?.created_by && row?.created_by.charAt(0)}
                          </Avatar>
                          {row?.created_by ? row?.created_by : "N/A"}
                        </div>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </>
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body1" color="textSecondary">
                    No data available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Stack
          spacing={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={filteredData?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
              {
                display: "none",
              },
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-toolbar": {
                paddingLeft: "16px",
              },
            }}
          />
          <Pagination
            count={Math.ceil(filteredData?.length / rowsPerPage)}
            showFirstButton
            showLastButton
            className="pagination-button"
            color="secondary"
            size="small"
            sx={{
              ".css-wjh20t-MuiPagination-ul": {
                justifyContent: "center",
              },
            }}
            onChange={(_, value) => handleChangePage(null, value - 1)}
            page={page + 1}
          />
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={filteredData?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            classes={{
              displayedRows: "hidden-displayed-rows", // Apply the custom class
            }}
            sx={{
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-input": {
                marginRight: "15px",
              },
            }}
          />
        </Stack>
      </TableContainer>

      {/* Pagination and other components can be added here */}
    </>
  )
}

export default ActivityNotesTable
